import { SLUG_MAX, WORKSPACE_SLUG, slugFrom } from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The setup flow's rules, apart from the screens — so they can be tested.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Each is the onboarding mockup's own function, translated: `slugify`, `slugErrOf`, `classify`,
 * the split in `addEmails`, and the file check in `onFile`.
 */

/**
 * ⚠️ **PNG and JPG, up to 2 MB** — the product owner ruled the type (*"PNG and JPG"*; the mockup also
 * took SVG for a logo), and the size is the server's: `user_avatar` and `workspace_icon` share a
 * 2 MiB cap, where the mockup says 5 MB. Saying 5 would be a promise the upload then breaks.
 */
export const IMAGE_TYPES = ['image/png', 'image/jpeg'] as const;
export const IMAGE_MAX_BYTES = 2 * 1024 * 1024;

export function imageProblem(file: { readonly type: string; readonly size: number }) {
  if (!(IMAGE_TYPES as readonly string[]).includes(file.type)) return 'type' as const;
  if (file.size > IMAGE_MAX_BYTES) return 'size' as const;
  return null;
}

/**
 * The address a workspace name suggests — *"Kovács Digital Kft."* → `kovacs-digital`.
 *
 * ⚠️ **The company form comes off first**, as the mockup's `slugify` does: nobody wants `-kft` in
 * every address they paste. Then `slugFrom`, the one function that folds Hungarian everywhere.
 */
export function addressFromName(name: string): string {
  const bare = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\b(kft|zrt|nyrt|bt|kkt|ev|ltd|gmbh|inc|llc)\b\.?/g, ' ');
  return slugFrom(bare).slice(0, 40).replace(/-+$/, '');
}

/** What is wrong with a typed address, before anybody is asked whether it is free. */
export function addressProblem(value: string) {
  if (/[^a-z0-9-]/.test(value)) return 'chars' as const;
  if (/^-|-$|--/.test(value)) return 'hyphen' as const;
  if (value.length < 3) return 'short' as const;
  /* The server's own expression, so nothing passes here that the column refuses. */
  if (value.length > SLUG_MAX || !WORKSPACE_SLUG.test(value)) return 'chars' as const;
  return null;
}

/** The mockup's `setSlug`: lower case, spaces to hyphens, at most 48 characters. */
export function typedAddress(raw: string): string {
  return raw.toLowerCase().replace(/\s+/g, '-').slice(0, 48);
}

/** Pasted or typed text, split into candidate addresses — commas, semicolons, any whitespace. */
export function splitAddresses(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((part) =>
      part
        .trim()
        .toLowerCase()
        .replace(/^[<"(]+|[>")]+$/g, ''),
    )
    .filter((part) => part !== '');
}

export type AddressVerdict =
  | { readonly status: 'ok' }
  | { readonly status: 'self' }
  | { readonly status: 'member' }
  | { readonly status: 'invalid'; readonly reason: 'at' | 'incomplete' | 'char' | 'domain' };

/**
 * One address's verdict. The mockup's `classify`, with the two lists it hard-coded passed in.
 *
 * ⚠️ **`self` and `member` are accepted and ignored, not refused** — the chip stays, muted, with
 * its reason, and no invitation goes to it. Refusing it red would tell somebody they made a
 * mistake by typing their own team.
 */
export function classifyAddress(
  email: string,
  self: string,
  members: ReadonlySet<string>,
): AddressVerdict {
  if (!email.includes('@')) return { status: 'invalid', reason: 'at' };
  const parts = email.split('@');
  const [local, domain] = parts;
  if (
    parts.length !== 2 ||
    local === undefined ||
    local === '' ||
    domain === undefined ||
    domain === ''
  ) {
    return { status: 'invalid', reason: 'incomplete' };
  }
  if (!/^[a-z0-9._%+-]+$/i.test(local) || !/^[a-z0-9.-]+$/i.test(domain)) {
    return { status: 'invalid', reason: 'char' };
  }
  if (!/\.[a-z]{2,}$/i.test(domain)) return { status: 'invalid', reason: 'domain' };
  if (email === self.toLowerCase()) return { status: 'self' };
  if (members.has(email)) return { status: 'member' };
  return { status: 'ok' };
}

/** Two letters from an address's local part — `petra.molnar@…` → `PM`. */
export function addressInitials(email: string): string {
  const parts = (email.split('@')[0] ?? '').split(/[._-]+/).filter((part) => part !== '');
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/**
 * The person's initials in the reader's order — `KA` for *Kovács Anna* in Hungarian, `AK` in
 * English — the mockup's `meIni`. Empty when neither half is typed yet.
 */
export function personInitials(family: string, given: string, locale: 'hu' | 'en'): string {
  const f = family.trim()[0] ?? '';
  const g = given.trim()[0] ?? '';
  return (locale === 'hu' ? f + g : g + f).toUpperCase();
}

/** The full name in the reader's order — `0101`'s ruling, as the rest of the product renders it. */
export function personName(family: string, given: string, locale: 'hu' | 'en'): string {
  const halves = locale === 'hu' ? [family.trim(), given.trim()] : [given.trim(), family.trim()];
  return halves.filter((half) => half !== '').join(' ');
}
